import React from "react"
import { cn } from "design_system/libs"
import AdventureStoryPreview from "@love-irish/adventure-story-preview"
import {
  BookSpine,
  type BookSpineThemeName,
  type BookSpineTheme,
} from "./book_spine"
import { twMerge } from "tailwind-merge"

// The minimal shape a cover needs. The connected shelf passes real GraphQL resources (which carry these
// fields on ItemInterface); this stays structural so the primitive works from stories/fixtures too.
export interface BookCoverItem {
  name?: string | null
  path?: string | null
  imageUrl?: string | null
  thumbnailUrl?: string | null
  /** English translation of the name (ItemInterface.translation) — shown as the spine subheader. */
  translation?: string | null
}

// A cover treatment: the frame/trim around a cover image. Mirrors the BOOK_SPINE_THEMES registry (and the
// shelf `props` sets) so covers and spines share one theming approach. `frameClassName` styles the wrapper
// (border/ring/shadow); `spineTheme` is the theme handed to the coverless spine fallback so a shelf reads
// consistently whether an item has a cover or not.
export interface BookCoverTheme {
  /** Classes on the cover wrapper (border/ring/padding/shadow). */
  frameClassName?: string
  /** Theme used for the coverless BookSpine fallback. */
  spineTheme?: BookSpineThemeName | BookSpineTheme
}

// Cover themes come from the shared element registry (element_themes.ts), keyed by element so a cover's
// frame + its coverless spine fallback match the rest of the shelf. A caller can still pass a custom
// BookCoverTheme. Extend by adding an element in element_themes.ts.
import { COVER_ELEMENT_THEMES, type ElementThemeName } from "./element_themes"

export const BOOK_COVER_THEMES = COVER_ELEMENT_THEMES
export type BookCoverThemeName = ElementThemeName
export const DEFAULT_BOOK_COVER_THEME: BookCoverTheme = COVER_ELEMENT_THEMES.default

const resolveTheme = (
  theme?: BookCoverThemeName | BookCoverTheme
): BookCoverTheme => {
  if (!theme) return DEFAULT_BOOK_COVER_THEME
  if (typeof theme === "string")
    return BOOK_COVER_THEMES[theme] ?? DEFAULT_BOOK_COVER_THEME
  return theme
}

export interface BookCoverProps {
  item: BookCoverItem
  /** A named built-in cover theme, or a custom BookCoverTheme. Defaults to `default`. */
  theme?: BookCoverThemeName | BookCoverTheme
  className?: string
}

/**
 * A cover-only book for the shelf. With a cover image it renders through AdventureStoryPreview (the same
 * book treatment as the book list-item); without one it falls back to a generated Celtic BookSpine. A
 * theme controls the cover frame/trim and the spine fallback's palette. The whole cover links to the
 * resource when it has a path.
 */
export const BookCover: React.FC<BookCoverProps> = ({
  item,
  theme,
  className,
}) => {
  const cover = item.thumbnailUrl || item.imageUrl
  const name = item.name || "Untitled"
  const t = resolveTheme(theme)

  const inner = cover ? (
    <AdventureStoryPreview
      title={
        // Explicit width (not just max-width) so the cover has a DEFINITE size: the whole slide chain
        // (img -> page flex-col -> w-fit anchor -> basis-auto slide) needs it, or mobile collapses the
        // cover to a tiny square (the img's intrinsic min) while spine/vinyl — which have fixed widths —
        // render fine. h-auto keeps the cover's aspect ratio.
        // 1px inset 50%-opacity ring so a light cover still reads against the page background.
        <img
          src={cover}
          alt={name}
          className="block w-[150px] h-auto ring-1 ring-inset ring-black/50"
        />
      }
      className={cn(
        "bg-secondary-100 p-0 relative h-auto border-0 min-h-fit!",
        t.frameClassName
      )}
    />
  ) : (
    <BookSpine name={name} subheader={item.translation} theme={t.spineTheme} />
  )

  // Lift the whole cover on hover. Only when there's a cover image — the coverless BookSpine fallback
  // brings its own lift, so we don't stack two translates.
  const body = (
    <span
      className={cn(
        "shrink-0",
        cover &&
          "transition-transform duration-300 ease-out motion-safe:hover:-translate-y-4 block",
        className
      )}
    >
      {inner}
    </span>
  )

  return item.path ? (
    // w-fit so the anchor reports the cover's fixed width to the flex row (iOS Safari collapses a plain
    // block anchor's intrinsic width to ~0 inside a basis-auto shelf slide; see bookshelf_section).
    <a href={item.path} aria-label={name} className="block w-fit">
      {body}
    </a>
  ) : (
    body
  )
}

export default BookCover
